import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShoppingCart, Check, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";

const KIT_LABEL = {
  set: "Set",
  "set+stud": "Set + stift",
  "set+thread": "Set + draadstang",
  din3567: "DIN 3567",
  "din3567-set": "DIN 3567 set",
  "din3567-half": "DIN 3567 half",
  half: "Halve beugel",
  cap: "Kapbeugel",
  other: "—",
};

/**
 * Compact horizontal row variant of ProductCard — visually similar to a
 * Windows Explorer "Details" view (small thumbnail left, all key info in
 * one tight row). Used on the finder-results page so 15+ matches fit on
 * screen without scrolling.
 *
 * Props mirror ProductCard. Renders only when `p.matched_variant` is set
 * (i.e. came from /api/match) — falls back to a hint if not.
 */
export default function ProductCardRow({ p, dnContext, d1MmContext, inchContext }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const params = new URLSearchParams();
  if (d1MmContext) params.set("d1_mm", d1MmContext);
  if (dnContext) params.set("dn", dnContext);
  if (inchContext) params.set("inch", inchContext);
  const qs = params.toString();
  const link = qs ? `/products/${p.id}?${qs}` : `/products/${p.id}`;
  const mv = p.matched_variant;
  const mats = (p.materials_available && p.materials_available.length > 0)
    ? p.materials_available
    : [p.material];
  const matLabel = (m) => (m === "galvanized" ? "GALVA" : (m || "").toUpperCase());

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mv || mv.price_on_request) return;
    addItem({
      product_id: p.id,
      sku: mv.sku,
      name: p.name_nl || p.name,
      image: p.image,
      material: mv.material || p.material,
      unit_price: Number(mv.price || 0),
      quantity: 1,
      meta: {
        dn: mv.dn,
        inch: mv.inch,
        diameter_mm: mv.diameter_mm,
        strip_size: mv.strip_size,
        box_qty: mv.box_qty,
        box_unit_price: mv.box_unit_price,
        piece_price: mv.price,
        pack_size: mv.pack_size || 0,
        subpack_surcharge: p.subpack_surcharge ?? 24.5,
      },
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Link
      to={link}
      data-testid={`product-card-row-${p.id}`}
      className="group flex items-stretch gap-3 rounded-md border border-zinc-200 bg-white p-2 transition hover:border-nibro-sage hover:shadow-sm sm:gap-4 sm:p-3"
    >
      {/* Thumbnail */}
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-100 bg-white sm:h-20 sm:w-20">
        <img
          src={p.image}
          alt={p.name_nl || p.name}
          loading="lazy"
          className="h-full w-full object-contain p-1.5"
        />
      </div>

      {/* Title + meta column */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-display text-sm font-bold text-zinc-900 leading-tight">
                {p.name_nl || p.name}
              </span>
              {mats.map((m) => (
                <span
                  key={m}
                  className="inline-flex rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-wider text-zinc-700"
                >
                  {matLabel(m)}
                </span>
              ))}
              {p.kit_type && (
                <span className="inline-flex rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-wider text-emerald-800">
                  {KIT_LABEL[p.kit_type] || "—"}
                </span>
              )}
              {p.is_half && (
                <span className="inline-flex items-center gap-0.5 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-mono text-[9px] font-bold text-amber-900">
                  <AlertTriangle size={9} /> 1/2
                </span>
              )}
            </div>
            {mv && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] text-zinc-600">
                <span className="font-bold text-nibro-sage-dark">
                  Ø {mv.diameter_mm} mm
                  {mv.dn ? ` · DN${mv.dn}` : ""}
                  {mv.inch ? ` · ${mv.inch}` : ""}
                </span>
                <span className="text-zinc-300">|</span>
                <span>{mv.sku}</span>
                {mv.strip_size && <span>· {mv.strip_size}</span>}
                {mv.box_qty > 0 && !mv.price_on_request && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-nibro-sage-pale px-1.5 py-0.5 font-bold text-nibro-sage-dark">
                    Omdoos {mv.box_qty}× · €{Number(mv.box_unit_price || 0).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Price + action column */}
      <div className="flex flex-shrink-0 flex-col items-end justify-between gap-2">
        {mv ? (
          mv.price_on_request ? (
            <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[10px] font-bold text-amber-800">
              Op aanvraag
            </span>
          ) : (
            <div className="text-right">
              <div className="font-display text-base font-bold text-emerald-700 leading-none">
                €{Number(mv.price || 0).toFixed(2)}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-400">/stuk</div>
            </div>
          )
        ) : (
          <div className="text-right font-display text-sm font-bold text-zinc-900">
            v.a. €{(p.price_from || 0).toFixed(2)}
          </div>
        )}
        {mv && !mv.price_on_request && (
          <button
            type="button"
            onClick={handleQuickAdd}
            data-testid={`product-card-row-quick-add-${p.id}`}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white transition ${
              added ? "bg-emerald-500" : "bg-nibro-sage hover:bg-nibro-sage-hover"
            }`}
            title="Direct bestellen"
          >
            {added ? (
              <><Check size={11} /> OK</>
            ) : (
              <><ShoppingCart size={11} /> Toevoegen</>
            )}
          </button>
        )}
        {mv && mv.price_on_request && (
          <Link
            to={`/contact?sku=${encodeURIComponent(mv.sku)}&subject=${encodeURIComponent("Offerteaanvraag " + mv.sku)}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`product-card-row-request-${p.id}`}
            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-amber-600"
          >
            Offerte
          </Link>
        )}
        <ChevronRight size={14} className="text-zinc-300 transition group-hover:translate-x-0.5 group-hover:text-nibro-sage" />
      </div>
    </Link>
  );
}
